/**
 * FoodMoodQuiz — Lightweight 2-tap modal quiz.
 * Step 1: Pick your vibe → Step 2: Pick your craving → AI generates mood → Result with share.
 */
import { useState, useRef, useCallback } from 'react';
import { X, Sparkles, Share2, Download, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VIBES = [
  { id: 'cozy', label: 'Cozy', emoji: '🛋', color: '#8B5E3C' },
  { id: 'energetic', label: 'Energetic', emoji: '⚡', color: '#FF6B35' },
  { id: 'adventurous', label: 'Adventurous', emoji: '🌍', color: '#2E7D32' },
  { id: 'romantic', label: 'Romantic', emoji: '🕯', color: '#C62828' },
  { id: 'chill', label: 'Chill', emoji: '🌊', color: '#0277BD' },
  { id: 'social', label: 'Social', emoji: '🎉', color: '#7B1FA2' },
  { id: 'nostalgic', label: 'Nostalgic', emoji: '📷', color: '#5D4037' },
  { id: 'creative', label: 'Creative', emoji: '🎨', color: '#E65100' },
];

const CRAVINGS = [
  { id: 'sweet', label: 'Sweet', emoji: '🍰', color: '#EC407A' },
  { id: 'savory', label: 'Savory', emoji: '🧀', color: '#FFA726' },
  { id: 'spicy', label: 'Spicy', emoji: '🌶', color: '#D32F2F' },
  { id: 'fresh', label: 'Fresh', emoji: '🥗', color: '#43A047' },
  { id: 'rich', label: 'Rich', emoji: '🍫', color: '#4E342E' },
  { id: 'light', label: 'Light', emoji: '🍃', color: '#66BB6A' },
  { id: 'umami', label: 'Umami', emoji: '🍜', color: '#F57C00' },
  { id: 'crunchy', label: 'Crunchy', emoji: '🥨', color: '#8D6E63' },
];

const FoodMoodQuiz = ({ open, onClose, locationString }) => {
  const [step, setStep] = useState(0); // 0=vibe, 1=craving, 2=loading, 3=result
  const [vibe, setVibe] = useState(null);
  const [craving, setCraving] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);
  const token = localStorage.getItem('fq_auth_token');

  const reset = useCallback(() => {
    setStep(0);
    setVibe(null);
    setCraving(null);
    setResult(null);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const selectVibe = (v) => {
    setVibe(v);
    setStep(1);
  };

  const selectCraving = async (c) => {
    const selectedVibe = vibe; // capture vibe before async
    setCraving(c);
    setStep(2);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/food-mood`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ vibe: selectedVibe?.id || 'adventurous', craving: c.id, location: locationString || '' }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setStep(3);
      } else {
        throw new Error('Failed');
      }
    } catch {
      setResult({
        mood_title: 'Flavor Explorer',
        mood_emoji: '🍽',
        mood_color: '#FF6B35',
        description: `You're feeling ${(selectedVibe?.label || 'adventurous').toLowerCase()} with a craving for something ${c.label.toLowerCase()}. The universe of flavor awaits!`,
        dish_picks: [
          { name: 'Chef\'s Surprise', why: 'Matches your adventurous spirit', cuisine: 'Fusion', emoji: '🍛' },
          { name: 'Comfort Classic', why: 'Hits that craving right', cuisine: 'Modern', emoji: '🥘' },
          { name: 'Sweet Finale', why: 'Every mood deserves dessert', cuisine: 'Patisserie', emoji: '🍰' },
        ],
        share_text: 'My food mood today: Flavor Explorer 🍽',
      });
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  // We need access to `vibe` inside selectCraving — captured directly in the function

  const handleShare = async () => {
    const text = result?.share_text || `My food mood: ${result?.mood_title}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My Food Mood', text, url: window.location.origin + '/surprise-me' });
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(text);
        alert('Copied to clipboard!');
      } catch {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        alert('Copied to clipboard!');
      }
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas || !result) return;

    const ctx = canvas.getContext('2d');
    const w = 1080, h = 1920;
    canvas.width = w;
    canvas.height = h;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, result.mood_color || '#FF6B35');
    grad.addColorStop(0.5, '#1a1a2e');
    grad.addColorStop(1, '#0f0f1a');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Mood emoji (large)
    ctx.font = '180px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(result.mood_emoji || '🍽', w / 2, 380);

    // Mood title
    ctx.font = 'bold 72px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(result.mood_title || 'Flavor Explorer', w / 2, 530);

    // Description (word wrap)
    ctx.font = '36px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    const desc = result.description || '';
    const words = desc.split(' ');
    let line = '', y = 640;
    for (const word of words) {
      const test = line + word + ' ';
      if (ctx.measureText(test).width > w - 160) {
        ctx.fillText(line.trim(), w / 2, y);
        line = word + ' ';
        y += 50;
      } else {
        line = test;
      }
    }
    if (line.trim()) ctx.fillText(line.trim(), w / 2, y);

    // Dish picks
    y += 100;
    ctx.font = 'bold 40px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText('Today\'s Picks', w / 2, y);
    y += 60;

    (result.dish_picks || []).forEach((d) => {
      ctx.font = '48px sans-serif';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText(`${d.emoji || '🍽'} ${d.name}`, w / 2, y);
      ctx.font = '30px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      y += 48;
      ctx.fillText(d.why || '', w / 2, y);
      y += 70;
    });

    // Branding
    ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.fillText('FoodQuest', w / 2, h - 120);
    ctx.font = '28px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('What\'s your food mood?', w / 2, h - 70);

    // Download
    const link = document.createElement('a');
    link.download = `food-mood-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="food-mood-overlay">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <div className="flex items-center gap-2">
            {step > 0 && step < 3 && (
              <button onClick={() => setStep(s => Math.max(0, s - 1))} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" data-testid="mood-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Sparkles className="w-5 h-5 text-[#FF6B35]" />
            <span className="font-bold text-gray-800">Food Mood</span>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" data-testid="mood-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 pb-3">
          {[0, 1].map(i => (
            <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${step >= i + 1 || (step === i && step < 2) ? (step > i ? 'w-8 bg-[#FF6B35]' : 'w-8 bg-[#FF6B35]/40') : 'w-3 bg-gray-200'}`} />
          ))}
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-6">

          {/* Step 0: Vibe */}
          {step === 0 && (
            <div className="animate-fade-in-up" data-testid="mood-step-vibe">
              <h2 className="text-xl font-bold text-gray-900 mb-1">What's your vibe right now?</h2>
              <p className="text-sm text-gray-500 mb-5">Pick one that matches your mood</p>
              <div className="grid grid-cols-2 gap-3">
                {VIBES.map(v => (
                  <button
                    key={v.id}
                    onClick={() => selectVibe(v)}
                    className="group flex items-center gap-3 px-4 py-4 bg-gray-50 hover:bg-gray-100 rounded-2xl transition-all hover:scale-[1.02] active:scale-95 border-2 border-transparent hover:border-gray-200"
                    data-testid={`vibe-${v.id}`}
                  >
                    <span className="text-2xl group-hover:scale-110 transition-transform">{v.emoji}</span>
                    <span className="font-semibold text-gray-700 text-sm">{v.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Craving */}
          {step === 1 && (
            <div className="animate-fade-in-up" data-testid="mood-step-craving">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{vibe?.emoji}</span>
                <span className="text-sm font-medium text-[#FF6B35]">{vibe?.label}</span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">What are you craving?</h2>
              <p className="text-sm text-gray-500 mb-5">Pick the flavor profile that calls to you</p>
              <div className="grid grid-cols-2 gap-3">
                {CRAVINGS.map(c => (
                  <button
                    key={c.id}
                    onClick={() => selectCraving(c)}
                    className="group flex items-center gap-3 px-4 py-4 bg-gray-50 hover:bg-gray-100 rounded-2xl transition-all hover:scale-[1.02] active:scale-95 border-2 border-transparent hover:border-gray-200"
                    data-testid={`craving-${c.id}`}
                  >
                    <span className="text-2xl group-hover:scale-110 transition-transform">{c.emoji}</span>
                    <span className="font-semibold text-gray-700 text-sm">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Loading */}
          {step === 2 && (
            <div className="flex flex-col items-center justify-center py-16 animate-fade-in-up" data-testid="mood-loading">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-[#FF6B35] animate-spin" />
                </div>
              </div>
              <p className="mt-6 text-lg font-bold text-gray-800">Reading your food aura...</p>
              <p className="mt-1 text-sm text-gray-500">
                {vibe?.emoji} {vibe?.label} + {craving?.emoji} {craving?.label}
              </p>
            </div>
          )}

          {/* Step 3: Result */}
          {step === 3 && result && (
            <div className="animate-fade-in-up" data-testid="mood-result">
              {/* Mood card */}
              <div className="rounded-2xl overflow-hidden mb-5" style={{ background: `linear-gradient(135deg, ${result.mood_color || '#FF6B35'}22, ${result.mood_color || '#FF6B35'}08)` }}>
                <div className="p-6 text-center">
                  <span className="text-6xl block mb-3">{result.mood_emoji || '🍽'}</span>
                  <h2 className="text-2xl font-extrabold text-gray-900 mb-2" data-testid="mood-title">{result.mood_title}</h2>
                  <p className="text-sm text-gray-600 leading-relaxed">{result.description}</p>
                </div>
              </div>

              {/* Dish picks */}
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Today's Picks For You</h3>
              <div className="space-y-2.5 mb-6">
                {(result.dish_picks || []).map((d, i) => (
                  <div key={i} className="flex items-start gap-3 p-3.5 bg-gray-50 rounded-xl" data-testid={`mood-pick-${i}`}>
                    <span className="text-2xl flex-shrink-0 mt-0.5">{d.emoji || '🍽'}</span>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 text-sm">{d.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{d.why}</p>
                      <span className="text-[10px] font-medium text-[#FF6B35] mt-1 inline-block">{d.cuisine}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-2 h-12 bg-[#FF6B35] hover:bg-[#E5592E] text-white rounded-xl font-bold text-sm transition-colors active:scale-95"
                  data-testid="mood-share-btn"
                >
                  <Share2 className="w-4 h-4" /> Share Mood
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center justify-center gap-2 h-12 px-5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-colors active:scale-95"
                  data-testid="mood-download-btn"
                >
                  <Download className="w-4 h-4" /> Save
                </button>
              </div>

              {/* Try again */}
              <button
                onClick={reset}
                className="w-full mt-3 text-center text-sm text-gray-400 hover:text-[#FF6B35] transition-colors py-2"
                data-testid="mood-retry-btn"
              >
                Try a different mood
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden canvas for share card generation */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default FoodMoodQuiz;
